import { useQuery } from '@apollo/client';
import type { SelectProps } from 'antd';
import { Select, Tag } from 'antd';
import { useField, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { SALES_REP_USERS } from '../../graphql/queries/user';
import colors from '../ColorMap/ColorMap';
import './SalesRepSelect.scss';

type TagRender = SelectProps['tagRender'];

interface OptionType {
  value: string;
  label: string;
}

interface User {
  id: string;
  fullName: string;
}

interface SalesRepFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  mode?: 'multiple' | 'tags';
  size?: 'large' | 'middle' | 'small';
  showSearch?: boolean;
  filterOption?: (input: string, option?: OptionType) => boolean;
}

const SalesRepField: React.FC<SalesRepFieldProps> = ({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  mode = 'multiple',
  size = 'large',
  showSearch = true,
  filterOption = (input, option) =>
    option?.label.toLowerCase().includes(input.toLowerCase()) ?? false,
}) => {
  const [field, meta] = useField(name);
  const formik = useFormikContext();
  const { loading, data } = useQuery(SALES_REP_USERS);

  const options = useMemo(() => {
    if (!data?.salesRepUsers) return [];
    return data.salesRepUsers.map((user: User) => ({
      value: user.id,
      label: user.fullName,
      ...user,
    }));
  }, [data]);

  const handleChange = (selectedValues: string[]): void => {
    const selectedOptions = options.filter((option) =>
      selectedValues.includes(option.value)
    );
    formik.setFieldValue(name, selectedOptions);
  };

  const handleBlur = (): void => {
    formik.setFieldTouched(name, true);
  };

  const currentValues = (field.value || []).map((item: { value: string }) => item.value);

  const tagRender: TagRender = (props) => {
    const { label: newLabel, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>): void => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color={colors['color-blue-90']}
        style={{
          display: 'flex',
          padding: '4px 4px 4px 8px',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '4px',
          backgroundColor: colors['color-blue-90'],
          color: 'white',
        }}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
      >
        {newLabel}
      </Tag>
    );
  };

  return (
    <div className="my-4">
      {label && (
        <label htmlFor={name} className="sales-rep-label">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}

      <Select
        mode={mode}
        size={size}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{ width: '100%', marginTop: '3px' }}
        options={options}
        value={currentValues}
        filterOption={filterOption}
        showSearch={showSearch}
        disabled={disabled || loading}
        loading={loading}
        status={meta.touched && meta.error ? 'error' : undefined}
        tagRender={tagRender}
      />

      {meta.touched && meta.error && (
        <div className="text-red-500 text-sm mt-1">
          {(() => {
            if (Array.isArray(meta.error)) return meta.error.join(', ');
            if (typeof meta.error === 'string') return meta.error;
            return 'Invalid selection';
          })()}
        </div>
      )}
    </div>
  );
};

export default SalesRepField;
